import { supabase } from '@/integrations/supabase/client';

/**
 * Patterns that identify ephemeral (short-lived) image URLs from Google Shopping,
 * SerpAPI thumbnails, and similar CDNs whose tokens expire after ~10-15 minutes.
 */
const EPHEMERAL_PATTERNS = [
  /encrypted-tbn\d*\.gstatic\.com/i,
  /gstatic\.com\/shopping/i,
  /serpapi\.com.*thumbnail/i,
  /googleusercontent\.com\/shopping/i,
];

/**
 * Returns true if the given URL is a known ephemeral image URL that will expire.
 */
export function isEphemeralImageUrl(url: string): boolean {
  if (!url) return false;
  return EPHEMERAL_PATTERNS.some((pattern) => pattern.test(url));
}

/**
 * If the image URL is ephemeral, proxies it through image-proxy with cache=true
 * to store a permanent copy in storage. Returns the permanent URL on success,
 * or falls back to the original URL on failure.
 */
export async function cacheImageIfNeeded(imageUrl: string): Promise<string> {
  if (!isEphemeralImageUrl(imageUrl)) return imageUrl;

  try {
    const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
    if (!projectId) {
      console.warn('[imageUtils] No project ID, skipping cache');
      return imageUrl;
    }

    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      console.warn('[imageUtils] No auth session, skipping cache');
      return imageUrl;
    }

    const proxyUrl = `https://${projectId}.supabase.co/functions/v1/image-proxy?url=${encodeURIComponent(imageUrl)}&cache=true`;

    const response = await fetch(proxyUrl, {
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      },
    });

    if (!response.ok) {
      console.warn('[imageUtils] Proxy cache failed:', response.status);
      return imageUrl;
    }

    const result = await response.json();
    if (result.url) {
      console.log('[imageUtils] Cached ephemeral image:', result.url);
      return result.url;
    }

    return imageUrl;
  } catch (error) {
    console.error('[imageUtils] Failed to cache ephemeral image:', error);
    return imageUrl;
  }
}
